import { Component, computed, effect, ElementRef, inject, signal, untracked } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { ContactService } from './services/contact-service';
import { LicenseOrderService, ORDER_DEADLINE_DAY } from './services/license-order-service';
import { LICENSE_TYPE_LABELS, LicenseType } from '../../school-license/models/school-license.model';
import {
  CREDIT_PACKAGE_OPTIONS,
  creditAmountLabel,
  creditPackageLabel,
  creditPackagePrice,
  DEFAULT_SIZE_BAND,
  licensePrice,
  ORDERABLE_TIERS,
  SchoolSizeBand,
  SIZE_OPTIONS,
  sizeLabel,
} from '../../school-license/models/license-pricing.model';
import { ApiErrorHandler } from '../errors/api-error-handler';
import { SUPPORT_EMAIL } from '../support-contact';
import { TRAINING_DISCOUNT_PERCENT, TRAINING_TITLE } from '../../home/training-card/training-card';
import { SchoolService } from '../../schools/services/school-service/school-service';
import { SchoolUserService } from '../../school-users/services/school-user-service/school-user-service';
import {
  SCHOOL_MESSAGE_ERRORS,
  schoolMessageComposer,
} from '../../school-users/school-message-form/school-message-composer';
import { apiErrorCode } from '../errors/api-error';
import { SchoolMessageForm } from '../../school-users/school-message-form/school-message-form';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Volný dotaz, předvyplněná objednávka licence, AI kreditů či školení, nebo zpráva kolegům ze školy. */
export type ChatMode = 'message' | 'license' | 'credits' | 'training' | 'colleagues';

export type TrainingFormat = 'online' | 'onsite';

export const TRAINING_FORMAT_LABELS: Record<TrainingFormat, string> = {
  online: 'Online',
  onsite: 'Prezenčně ve škole',
};

@Component({
  selector: 'app-floating-chat',
  imports: [
    MatButtonModule,
    MatIcon,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    FormsModule,
    SchoolMessageForm,
  ],
  templateUrl: './floating-chat.html',
  host: {
    '(document:click)': 'closeMenuOnOutsideClick($event)',
    '(document:keydown.escape)': 'menuOpen.set(false)',
  },
  styles: `
    .chat-fab {
      box-shadow:
        0 2px 6px rgb(0 0 0 / 0.14),
        0 8px 20px -6px rgb(0 0 0 / 0.28);
      transition:
        transform 150ms ease-out,
        box-shadow 150ms ease-out,
        filter 150ms ease-out;
    }

    .chat-fab:hover {
      filter: brightness(1.06);
      transform: translateY(-1px);
      box-shadow:
        0 3px 8px rgb(0 0 0 / 0.16),
        0 12px 26px -8px rgb(0 0 0 / 0.32);
    }

    .chat-fab:active {
      filter: brightness(0.96);
      transform: translateY(0);
      box-shadow:
        0 1px 3px rgb(0 0 0 / 0.14),
        0 4px 12px -6px rgb(0 0 0 / 0.28);
    }

    .chat-send:not([disabled]) {
      box-shadow:
        0 2px 6px rgb(0 0 0 / 0.14),
        0 8px 20px -6px rgb(0 0 0 / 0.28);
      transition:
        transform 150ms ease-out,
        box-shadow 150ms ease-out,
        filter 150ms ease-out;
    }

    .chat-send:not([disabled]):hover {
      filter: brightness(1.06);
      transform: translateY(-1px);
      box-shadow:
        0 3px 8px rgb(0 0 0 / 0.16),
        0 12px 26px -8px rgb(0 0 0 / 0.32);
    }

    .chat-send:not([disabled]):active {
      filter: brightness(0.96);
      transform: translateY(0);
      box-shadow:
        0 1px 3px rgb(0 0 0 / 0.14),
        0 4px 12px -6px rgb(0 0 0 / 0.28);
    }

    .chat-panel {
      box-shadow:
        0 2px 6px rgb(0 0 0 / 0.12),
        0 16px 32px -12px rgb(0 0 0 / 0.24);
    }

    .chat-header {
      box-shadow: 0 1px 0 rgb(0 0 0 / 0.06);
    }

    .chat-avatar {
      background-color: rgb(255 255 255 / 0.2);
    }

    @media (prefers-reduced-motion: reduce) {
      .chat-fab,
      .chat-send {
        transition: none;
      }
    }
  `,
})
export class FloatingChat {
  private readonly authService = inject(AuthService);
  private readonly contactService = inject(ContactService);
  private readonly licenseOrder = inject(LicenseOrderService);
  private readonly apiErrors = inject(ApiErrorHandler);
  private readonly schoolService = inject(SchoolService);
  private readonly schoolUserService = inject(SchoolUserService);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly supportEmail = SUPPORT_EMAIL;
  readonly isOpen = signal(false);
  /** The choice between support and colleagues, shown before either opens. */
  readonly menuOpen = signal(false);

  /**
   * The school whose members the user can write to: the one picked in the header, provided they
   * belong to it. A system admin looking at someone else's school has no colleagues there, and gets
   * the support panel straight away, as everyone did before.
   */
  readonly colleagueSchoolId = computed(() => {
    const school = this.schoolService.selectedSchool();
    return school?.schoolRole ? school.id : null;
  });

  /** Members are fetched only once the colleagues form is open, not for every page view. */
  readonly colleagueMessage = schoolMessageComposer(() =>
    this.mode() === 'colleagues' ? this.colleagueSchoolId() : null,
  );

  /** "Napište nám" is a promise of support; with colleagues on offer too, it would mislead. */
  readonly fabLabel = computed(() => {
    if (this.isOpen() || this.menuOpen()) return 'Zavřít';
    return this.colleagueSchoolId() !== null ? 'Napsat zprávu' : 'Napište nám';
  });
  readonly fromEmail = computed(() => this.authService.currentUser()?.email ?? '');
  readonly subject = signal('');
  readonly body = signal('');
  readonly sent = signal(false);
  readonly sending = signal(false);

  readonly mode = signal<ChatMode>('message');
  readonly orderableTiers = ORDERABLE_TIERS;
  readonly tier = signal<LicenseType>('start');
  readonly monthOptions = this.licenseOrder.monthOptions;
  readonly sizeOptions = SIZE_OPTIONS;
  readonly size = signal<SchoolSizeBand>(DEFAULT_SIZE_BAND);
  readonly month = signal(this.licenseOrder.monthOptions()[0].value);
  readonly orderDeadlineDay = ORDER_DEADLINE_DAY;
  readonly billingLoading = this.licenseOrder.isLoading;

  readonly creditPackageOptions = CREDIT_PACKAGE_OPTIONS;
  readonly packages = signal(CREDIT_PACKAGE_OPTIONS[0]);

  readonly trainingFormats = Object.keys(TRAINING_FORMAT_LABELS) as TrainingFormat[];
  readonly trainingFormat = signal<TrainingFormat>('online');
  readonly trainingDiscountPercent = TRAINING_DISCOUNT_PERCENT;

  /** Velikost a cena jednoho balíčku — v nabídce i v poznámce pod objednávkou. */
  readonly creditPackageSizeLabel = creditAmountLabel(1);
  readonly creditPackagePriceLabel = creditPackagePrice(1);

  /** Cena tarifu pro vybranou velikost školy, tak jak půjde do zprávy. */
  readonly priceLabel = computed(() => licensePrice(this.tier(), this.size()));

  /** Cena za vybraný počet balíčků kreditů, tak jak půjde do zprávy. */
  readonly creditPriceLabel = computed(() => creditPackagePrice(this.packages()));
  readonly creditAmount = computed(() => creditAmountLabel(this.packages()));
  readonly hasBillingDetails = this.licenseOrder.hasBillingDetails;

  /** Objednávka dává smysl jen přihlášenému uživateli — bez školy není co fakturovat. */
  readonly canOrderLicense = this.authService.isAuthenticated;

  readonly headline = computed(() => {
    switch (this.mode()) {
      case 'license':
        return 'Objednávka licence';
      case 'credits':
        return 'Objednávka AI kreditů';
      case 'training':
        return 'Objednávka školení';
      case 'colleagues':
        return 'Zpráva kolegům';
      default:
        return 'Napište nám';
    }
  });

  readonly subline = computed(() => {
    switch (this.mode()) {
      case 'message':
        return 'Rádi vám pomůžeme';
      case 'colleagues':
        return this.schoolService.selectedSchool()?.name ?? '';
      default:
        return 'Ozveme se s podrobnostmi';
    }
  });

  constructor() {
    // The header moved to a school the user does not belong to — nobody left to write to there.
    effect(() => {
      if (this.mode() === 'colleagues' && this.colleagueSchoolId() === null) {
        untracked(() => this.backToMessage());
      }
    });

    // Text objednávky se drží vybraného tarifu, měsíce i načtených fakturačních údajů. Uživatel ho
    // může přepsat, ale další změna výběru ho složí znovu — jinak by odešla zpráva, která
    // s vybraným tarifem nesouhlasí.
    effect(() => {
      if (this.mode() !== 'license') return;
      const tier = this.tier();
      const month = this.month();
      const size = this.size();
      const billingLines = this.licenseOrder.billingLines();
      this.compose(
        `license|${tier}|${month}|${size}`,
        this.orderSubject(tier, month),
        this.orderBody(tier, month, size, billingLines),
      );
    });

    // Totéž pro objednávku kreditů — text se drží vybraného počtu balíčků i fakturačních údajů.
    effect(() => {
      if (this.mode() !== 'credits') return;
      const packages = this.packages();
      const billingLines = this.licenseOrder.billingLines();
      this.compose(
        `credits|${packages}`,
        `Objednávka AI kreditů — ${creditAmountLabel(packages)}`,
        this.creditOrderBody(packages, billingLines),
      );
    });

    // Totéž pro objednávku školení — text se drží vybrané formy i fakturačních údajů.
    effect(() => {
      if (this.mode() !== 'training') return;
      const format = this.trainingFormat();
      const billingLines = this.licenseOrder.billingLines();
      this.compose(
        `training|${format}`,
        `Objednávka školení ${TRAINING_TITLE} — ${TRAINING_FORMAT_LABELS[format].toLowerCase()}`,
        this.trainingOrderBody(format, billingLines),
      );
    });

    // Objednávka vyvolaná zvenčí (stránka licencí). `untracked` je tu nutné: otevření formuláře
    // čte nabídku měsíců, kterou si `requestOrder` právě přepočítal — bez něj by se effect na tom
    // signálu zavěsil a spouštěl sám sebe.
    effect(() => {
      if (this.licenseOrder.orderRequests() === 0) return;
      untracked(() => {
        this.sent.set(false);
        if (this.licenseOrder.requestedKind() === 'credits') {
          this.mode.set('credits');
          this.isOpen.set(true);
          return;
        }
        const requestedTier = this.licenseOrder.requestedTier();
        if (requestedTier !== null) {
          this.tier.set(requestedTier);
        }
        const requestedSize = this.licenseOrder.requestedSize();
        if (requestedSize !== null) {
          this.size.set(requestedSize);
        }
        this.openOrderForm();
        this.isOpen.set(true);
      });
    });
  }

  /** Výběr, ze kterého vznikl text v poli, a text samotný — viz {@link compose}. */
  private composedFrom = '';
  private composedSubject = '';
  private composedBody = '';

  /**
   * Složí text objednávky, ale nepřepíše, co do něj napsal uživatel. Změna výběru (`key`) ho
   * přepsat musí — jinak by odešla objednávka, která s vybraným tarifem nesouhlasí. Fakturační
   * údaje se ale dotahují zvlášť a dorazí až po otevření formuláře; ty jen doplní text, který
   * zůstal přesně takový, jak ho tenhle formulář naposledy složil.
   */
  private compose(key: string, subject: string, body: string): void {
    const selectionChanged = key !== this.composedFrom;
    this.composedFrom = key;

    if (selectionChanged || untracked(this.subject) === this.composedSubject) {
      this.subject.set(subject);
      this.composedSubject = subject;
    }
    if (selectionChanged || untracked(this.body) === this.composedBody) {
      this.body.set(body);
      this.composedBody = body;
    }
  }

  /** Prázdný formulář je zase složitelný — text v něm není uživatelova úprava. */
  private clearComposed(): void {
    this.subject.set('');
    this.body.set('');
    this.composedFrom = '';
    this.composedSubject = '';
    this.composedBody = '';
  }

  tierLabel(type: LicenseType): string {
    return LICENSE_TYPE_LABELS[type];
  }

  startLicenseOrder(): void {
    this.licenseOrder.enable();
    this.openOrderForm();
  }

  creditPackageLabel(packages: number): string {
    return creditPackageLabel(packages);
  }

  startCreditOrder(): void {
    // Fakturační údaje se stahují stejně jako u licence — objednávka kreditů se fakturuje škole.
    this.licenseOrder.enable();
    this.mode.set('credits');
  }

  trainingFormatLabel(format: TrainingFormat): string {
    return TRAINING_FORMAT_LABELS[format];
  }

  startTrainingOrder(): void {
    // Školení se fakturuje škole stejně jako licence — fakturační údaje se doplní z jejího profilu.
    this.licenseOrder.enable();
    this.mode.set('training');
  }

  private openOrderForm(): void {
    // Nabídka měsíců se otevřením přepočítala — dřív vybraný měsíc v ní už být nemusí, a select by
    // pak zůstal prázdný.
    this.month.set(this.monthOptions()[0].value);
    this.mode.set('license');
  }

  backToMessage(): void {
    this.mode.set('message');
    this.clearComposed();
  }

  readonly canSend = computed(
    () =>
      EMAIL_PATTERN.test(this.fromEmail().trim()) &&
      (this.subject().trim().length > 0 || this.body().trim().length > 0),
  );

  /** The floating button: closes whatever is open, else offers the choice, else opens support. */
  toggle(): void {
    if (this.isOpen()) {
      this.isOpen.set(false);
    } else if (this.colleagueSchoolId() !== null) {
      this.menuOpen.update((open) => !open);
    } else {
      this.isOpen.set(true);
    }
  }

  openSupport(): void {
    if (this.mode() === 'colleagues') {
      this.backToMessage();
    }
    this.sent.set(false);
    this.menuOpen.set(false);
    this.isOpen.set(true);
  }

  openColleagues(): void {
    this.mode.set('colleagues');
    this.sent.set(false);
    this.menuOpen.set(false);
    this.isOpen.set(true);
  }

  async sendToColleagues(): Promise<void> {
    const schoolId = this.colleagueSchoolId();
    if (schoolId === null || this.sending() || !this.colleagueMessage.validate()) {
      return;
    }

    this.sending.set(true);
    try {
      const result = await firstValueFrom(
        this.schoolUserService.sendMessage(schoolId, this.colleagueMessage.model()),
      );
      if (this.colleagueMessage.settle(result)) {
        this.colleagueMessage.reset();
        this.sent.set(true);
      }
    } catch (err) {
      this.colleagueMessage.handleSendFailure(apiErrorCode(err));
      this.apiErrors.notify(err, 'FloatingChat.sendToColleagues', SCHOOL_MESSAGE_ERRORS);
    } finally {
      this.sending.set(false);
    }
  }

  closeMenuOnOutsideClick(event: MouseEvent): void {
    if (this.menuOpen() && !this.host.nativeElement.contains(event.target as Node)) {
      this.menuOpen.set(false);
    }
  }

  writeAnother(): void {
    if (this.mode() !== 'colleagues') {
      this.mode.set('message');
      this.clearComposed();
    }
    this.sent.set(false);
  }

  async send(): Promise<void> {
    if (!this.canSend() || this.sending()) {
      return;
    }

    this.sending.set(true);
    try {
      await this.contactService.send({
        fromEmail: this.fromEmail().trim(),
        subject: this.subject().trim() || undefined,
        body: this.body().trim() || undefined,
      });
      this.sent.set(true);
    } catch (err) {
      this.apiErrors.notify(err, 'FloatingChat.send');
    } finally {
      this.sending.set(false);
    }
  }

  private monthLabel(value: string): string {
    return this.monthOptions().find((option) => option.value === value)?.label ?? value;
  }

  private orderSubject(tier: LicenseType, month: string): string {
    return `Objednávka licence ${LICENSE_TYPE_LABELS[tier]} od ${this.monthLabel(month)}`;
  }

  private orderBody(
    tier: LicenseType,
    month: string,
    size: SchoolSizeBand,
    billingLines: string[],
  ): string {
    const parts = [
      'Dobrý den,',
      `máme zájem o zakoupení licence ${LICENSE_TYPE_LABELS[tier]} od ${this.monthLabel(month)}.`,
      [`Velikost školy: ${sizeLabel(size)}`, `Cena dle ceníku: ${licensePrice(tier, size)}`].join(
        '\n',
      ),
    ];
    if (billingLines.length > 0) {
      parts.push(['Fakturační údaje:', ...billingLines].join('\n'));
    }
    parts.push('Prosíme o zaslání nabídky a dalších informací.', 'Děkujeme.');
    return parts.join('\n\n');
  }

  private creditOrderBody(packages: number, billingLines: string[]): string {
    const parts = [
      'Dobrý den,',
      'máme zájem o přikoupení AI kreditů nad rámec licence.',
      [
        `Počet balíčků: ${packages} (${creditAmountLabel(packages)})`,
        `Cena dle ceníku: ${creditPackagePrice(packages)}`,
      ].join('\n'),
    ];
    if (billingLines.length > 0) {
      parts.push(['Fakturační údaje:', ...billingLines].join('\n'));
    }
    parts.push('Prosíme o zaslání nabídky a dalších informací.', 'Děkujeme.');
    return parts.join('\n\n');
  }

  private trainingOrderBody(format: TrainingFormat, billingLines: string[]): string {
    const parts = [
      'Dobrý den,',
      `máme zájem o celosborové školení „${TRAINING_TITLE}“.`,
      `Forma: ${TRAINING_FORMAT_LABELS[format]}`,
      `Jako uživatelé Mapy výuky prosíme o uplatnění slevy ${TRAINING_DISCOUNT_PERCENT} %.`,
    ];
    if (billingLines.length > 0) {
      parts.push(['Fakturační údaje:', ...billingLines].join('\n'));
    }
    parts.push('Prosíme o zaslání cenové nabídky a návrhu termínu.', 'Děkujeme.');
    return parts.join('\n\n');
  }
}
